<!--
  Device-handoff header icon (Feature 42). A small, ALWAYS-REACHABLE indicator
  that something is playing on another household device — replaces the full-width
  HandoffBanner everywhere except the desktop homepage. Crucially it lives in the
  header, so in TV mode the D-pad can actually reach its "Continue here" / dismiss
  actions (the old banner sat outside spatial-nav and its buttons were
  unreachable, and it covered page content).

  Renders nothing when no foreign session is active.
-->
<template>
  <div v-if="handoff.hasActive" ref="root" class="relative">
    <button
      type="button"
      class="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      :class="{ 'bg-gray-100 dark:bg-gray-700': open }"
      :data-tv-focusable="uiStore.tvMode ? 'true' : null"
      :aria-label="`Playing on another device — ${handoff.activeSessions.length} session(s)`"
      :title="uiStore.tvMode ? '' : 'Playing on another device'"
      @click.stop="open = !open"
    >
      <!-- cast / screen icon -->
      <svg class="w-5 h-5 text-gray-600 dark:text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M2 8V6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-6" />
        <path d="M2 12a9 9 0 0 1 8 8" />
        <path d="M2 16a5 5 0 0 1 4 4" />
        <line x1="2" y1="20" x2="2.01" y2="20" />
      </svg>
      <!-- live pip -->
      <span
        class="absolute top-1 right-1 inline-block w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-gray-900 animate-pulse"
        aria-hidden="true"
      />
    </button>

    <!-- dropdown -->
    <div
      v-if="open"
      class="absolute right-0 mt-2 w-80 max-w-[calc(100vw-1rem)] bg-white dark:bg-gray-800 rounded-xl shadow-2xl ring-1 ring-black/5 dark:ring-white/10 z-50 overflow-hidden"
      :data-tv-row="uiStore.tvMode ? 'handoff-menu' : null"
    >
      <div class="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700">
        <p class="text-sm font-semibold text-gray-900 dark:text-white">Playing elsewhere</p>
        <p class="text-xs text-gray-500 dark:text-gray-400">On another device in your household</p>
      </div>
      <ul class="max-h-80 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
        <li v-for="s in handoff.activeSessions" :key="s.deviceId + ':' + s.fileKey" class="px-4 py-3">
          <div class="flex items-start gap-3">
            <img
              v-if="s.poster"
              :src="s.poster"
              :alt="s.title"
              loading="lazy"
              class="w-9 h-[3.4rem] object-cover rounded-md shrink-0 bg-gray-200 dark:bg-gray-700"
            />
            <div class="min-w-0 flex-1">
              <p class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ s.title }}</p>
              <p class="text-xs text-gray-500 dark:text-gray-400 truncate">
                ▶ on {{ s.deviceName || 'another device' }}
              </p>
              <div class="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  data-handoff-continue
                  class="px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                  :data-tv-focusable="uiStore.tvMode ? 'true' : null"
                  @click.stop="continueHere(s)"
                >
                  Continue here →
                </button>
                <button
                  type="button"
                  class="px-2 py-1.5 text-xs font-medium rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  :data-tv-focusable="uiStore.tvMode ? 'true' : null"
                  @click.stop="handoff.dismiss(s)"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useHandoffStore, isLocalRoute } from '@/stores/handoff'
import { useUiStore } from '@/stores/ui'

const router = useRouter()
const handoff = useHandoffStore()
const uiStore = useUiStore()

const root = ref(null)
const open = ref(false)

function continueHere(s) {
  if (!s || !isLocalRoute(s.route)) return
  const sep = s.route.includes('?') ? '&' : '?'
  router.push(s.route + sep + 'handoff=1')
  open.value = false
}

// In TV mode, move D-pad focus straight to "Continue here" when the dropdown
// opens — the whole point of #330 is that these actions are reachable now.
watch(open, (isOpen) => {
  if (!isOpen || !uiStore.tvMode) return
  nextTick(() => {
    const btn = root.value && root.value.querySelector('[data-handoff-continue]')
    if (btn) {
      try { btn.focus({ focusVisible: true }) } catch { btn.focus() }
    }
  })
})

function onClickOutside(event) {
  if (!open.value) return
  const el = root.value
  if (!el) return
  const path = typeof event.composedPath === 'function' ? event.composedPath() : null
  const inside = path ? path.includes(el) : el.contains(event.target)
  if (!inside) open.value = false
}

function onKeydown(event) {
  if (event.key === 'Escape' && open.value) open.value = false
}

onMounted(() => {
  handoff.startPolling()
  document.addEventListener('click', onClickOutside)
  document.addEventListener('keydown', onKeydown)
})

onBeforeUnmount(() => {
  handoff.stopPolling()
  document.removeEventListener('click', onClickOutside)
  document.removeEventListener('keydown', onKeydown)
})
</script>
